import { useEffect, useState } from "react";
import { Loader2, User } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";

/**
 * Lets the signed-in user edit their display name. Shows up alongside
 * change-email on dashboard settings pages.
 *
 * The display_name column on profiles is what every roster, audit log,
 * and admin tab renders for this user - keeping it editable lets people
 * fix typos from sign-up, change after marriage, etc, without us having
 * to manually update via SQL.
 *
 * Length: 1-60 chars, trimmed. Same shape as the rest of the app
 * (admin profile creation, venue admin promotion). Stripped to NULL on
 * submit if the field is empty so it falls back to email everywhere.
 */
export function DisplayNameSection() {
  const { profile, refreshProfile } = useAuth();
  const [value, setValue] = useState(profile?.display_name ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(profile?.display_name ?? "");
  }, [profile?.display_name]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length > 60) {
      return toast.error("Display name maxes out at 60 characters.");
    }
    if (trimmed === (profile?.display_name ?? "")) {
      return toast("No change to save.");
    }

    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: trimmed === "" ? null : trimmed })
      .eq("id", profile!.id);
    setSaving(false);
    if (error) return toast.error(error.message);

    toast.success("Display name updated.");
    // Refresh the in-memory profile so the sidebar avatar updates
    // without a full page reload.
    if (typeof refreshProfile === "function") {
      await refreshProfile();
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="mb-1 flex items-center gap-2">
        <User className="h-4 w-4 text-volt" />
        <h2 className="text-sm font-bold uppercase tracking-wider">
          Display name
        </h2>
      </div>
      <p className="mb-5 text-sm text-muted-foreground">
        How your name shows up on rosters, audit logs, and admin tabs.
        Leave blank to fall back to your email address.
      </p>

      <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
        <Input
          type="text"
          placeholder="Your name"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={60}
          disabled={saving}
          className="flex-1"
        />
        <Button type="submit" disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving…
            </>
          ) : (
            <>Save</>
          )}
        </Button>
      </form>
    </section>
  );
}
